import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ShieldAlert, Lock, Fingerprint, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COULEURS = {
  SQLi: "bg-red-100 text-red-700 border-red-200",
  XSS: "bg-orange-100 text-orange-700 border-orange-200",
  LFI: "bg-amber-100 text-amber-800 border-amber-200",
  RFI: "bg-amber-100 text-amber-800 border-amber-200",
  CMDi: "bg-red-100 text-red-700 border-red-200",
  SCANNER: "bg-purple-100 text-purple-700 border-purple-200",
  HONEYPOT: "bg-stone-900 text-white border-stone-900",
  CLONAGE: "bg-stone-900 text-white border-stone-900",
  EMPREINTE_DIVERGENTE: "bg-stone-800 text-white border-stone-800",
  RATE_LIMIT: "bg-sky-100 text-sky-700 border-sky-200",
  BANNISSEMENT: "bg-sky-900 text-white border-sky-900",
  UA_ABSENT: "bg-stone-200 text-stone-700 border-stone-300",
};

export const SecuritePanel = ({ adminId }) => {
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(false);

  const charger = useCallback(() => {
    setChargement(true);
    axios
      .get(`${API}/admin/securite`, { params: { admin_id: adminId, limite: 60 } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setChargement(false));
  }, [adminId]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 30000);
    return () => clearInterval(t);
  }, [charger]);

  const carte = (label, valeur, ton = "text-stone-900") => (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
      <div className={`font-heading text-3xl font-extrabold ${ton}`}>{valeur ?? "—"}</div>
      <div className="text-xs font-medium text-stone-500 mt-1">{label}</div>
    </div>
  );

  return (
    <section className="mt-14" data-testid="securite-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2.5">
          <ShieldAlert size={24} className="text-red-600" /> Sécurité — WAF Bastion
        </h2>
        <button
          onClick={charger}
          data-testid="securite-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-stone-900 border border-[#E5DFD5] bg-white rounded-full px-3 py-1.5"
        >
          <RefreshCw size={13} className={chargement ? "animate-spin" : ""} /> Rafraîchir
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="securite-stats">
        {carte("Attaques bloquées", data?.total)}
        {carte("Dernières 24 h", data?.dernieres_24h, "text-red-600")}
        {carte("Tentatives de clonage", data?.clonages, "text-stone-900")}
        {carte("IP hostiles suivies", data?.top_ip?.length)}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs" data-testid="securite-chiffrement">
        <span className="inline-flex items-center gap-1.5 bg-[#0A5C36]/10 text-[#0A5C36] border border-[#0A5C36]/20 rounded-full px-3 py-1 font-semibold">
          <Lock size={12} /> PCI DSS · {data?.chiffrement || "…"}
        </span>
        <span className="inline-flex items-center gap-1.5 bg-white border border-[#E5DFD5] rounded-full px-3 py-1 text-stone-600">
          <Fingerprint size={12} /> Hôtes légitimes : {(data?.hotes_autorises || []).join(", ") || "—"}
        </span>
      </div>

      {data?.par_type?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="securite-par-type">
          {data.par_type.map((t) => (
            <span
              key={t.type}
              className={`text-xs font-semibold border rounded-full px-3 py-1 ${COULEURS[t.type] || "bg-stone-100 text-stone-700 border-stone-200"}`}
            >
              {t.type} · {t.n}
            </span>
          ))}
        </div>
      )}

      <div className="mt-6">
        <h3 className="font-semibold text-stone-900 text-sm">Journal des blocages (temps réel)</h3>
        <div className="mt-3 space-y-1.5" data-testid="securite-evenements">
          {!data?.evenements?.length ? (
            <p className="text-sm text-stone-400">Aucune attaque détectée. Le bouclier est actif.</p>
          ) : (
            data.evenements.map((e) => (
              <div
                key={e.id}
                className="bg-white border border-[#E5DFD5] rounded-lg px-3 py-2 text-xs text-stone-600 flex flex-wrap items-center gap-x-2 gap-y-1"
              >
                <span className="text-stone-400">{new Date(e.created_at).toLocaleString("fr-FR")}</span>
                <span className={`font-bold border rounded px-1.5 ${COULEURS[e.type] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
                  {e.type}
                </span>
                <b className="text-stone-900">{e.methode} {e.chemin}</b>
                <span className="text-stone-400">· IP {e.ip} · {e.owasp}</span>
                <code className="text-red-600 break-all">{e.preuve}</code>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
